'use client';

import { useGetBottomToolbar } from "@/services/footer";
import { bottomToolbarAtom } from "@/store/footer";
import { footerAnimateConfigAtom } from "@/store/global-config";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import clsx from "clsx";
import { showFooterPages } from "@/const/page-url";
import activeIcon from '@/features/home/images/footer-active.png';
import { AudioId, useAudioHelper } from "@/utils/audio-hepler";
import { isRummyActiveAtom } from "@/app/[locale]/(main)/(non-activity)/rummy/store";
import { track } from "@/utils/track";
import { isFantasyActiveAtom } from "@/app/[locale]/(main)/(non-activity)/fantasy/store";
import { Link, useRouter, usePathname } from "@/i18n/navigation";
import { isJumpAction, jumpByAction } from "@/utils/jump-url";
import { useAppSafearea } from "@/hooks/use-app-safearea";
import { ActivitySourceEnum } from "@/const/report";
import IplFooterAnimate from "@/features/home/components/ipl-footer-animate";
import {
  buildFooterAnimationMatcher,
  type FooterAnimationMatcher,
} from "@/features/home/components/ipl-footer-animate/matcher";
import type { BottomToolbarRes } from "@/types/api/hall/bottom-toolbar";

type BottomToolbarItem = NonNullable<BottomToolbarRes['list']>[number];

export default function Footer() {
  const [barList, setBottomToolbar] = useAtom(bottomToolbarAtom);
  const isRummyActive = useAtomValue(isRummyActiveAtom);
  const isFantasyActive = useAtomValue(isFantasyActiveAtom);
  const footerAnimateConfig = useAtomValue(footerAnimateConfigAtom);
  const { playAudio } = useAudioHelper({ id: AudioId.TurnPage });
  const { data: bottomToolbar } = useGetBottomToolbar({
    queryConfig: {
      gcTime: 0,              // 垃圾回收时间为 0（立即清除缓存）
      staleTime: 0,           // 数据立即过期（总是重新请求）
      refetchOnMount: 'always',      // 每次组件挂载都重新请求
      refetchOnWindowFocus: true,    // 窗口聚焦时重新请求
      refetchOnReconnect: true,      // 网络重连时重新请求
    }
  });
  const pathname = usePathname(); // next-intl 的 usePathname 已经自动去除 locale 前缀
  const router = useRouter();

  useEffect(() => {
    setBottomToolbar(bottomToolbar?.list ?? []);
  }, [bottomToolbar, setBottomToolbar]);

  // 主动预加载底部导航栏的所有页面(这些是高频访问页面)
  useEffect(() => {
    if (barList && barList.length > 0) {
      barList.forEach((item) => {
        if (item.jump_link) {
          router.prefetch(item.jump_link);
        }
      });
    }
  }, [barList, router]);

  const animationMatcher = useMemo(
    () => buildFooterAnimationMatcher(footerAnimateConfig),
    [footerAnimateConfig]
  );

  const showFooter = useMemo(() => {
    if (isRummyActive || isFantasyActive) {
      return false;
    }
    // 特殊处理，如果当前路径包含/play，则不显示footer
    if (pathname.includes('/play')) {
      return false;
    }
    const isInPages = showFooterPages.includes(pathname);
    return Boolean(barList?.length && barList?.length > 0) && isInPages;
  }, [barList, pathname, isRummyActive, isFantasyActive]);

  const handleJump = useCallback((e, item) => {
    playAudio();

    const { jump_link } = item;

    // 处理动作链接
    if (isJumpAction(jump_link)) {
      e?.preventDefault();
      jumpByAction(jump_link, ActivitySourceEnum.bottomTab);
    } else if (!jump_link) {
      e?.preventDefault();
      router.push(`/banner-detail?id=${item.id}&type=2`);
    }
  }, [playAudio, router]);

  const { bottomSafe } = useAppSafearea();

  return (
    <>
      {
        showFooter && (
          <>
            <div className="w-full pb-[60px] rounded-t-[15px]"
              style={{
                paddingBottom: 60 + Number(bottomSafe || 0) + 'px'
              }}
            ></div>
            <div className="fixed bottom-0 left-0 right-0 z-20 w-full rounded-t-[15px] h-[70px] flex justify-between items-center"
              style={{
                background: 'linear-gradient(0deg, #101219, #262B38)',
                borderTop: '1px solid #3E4452',
                bottom: bottomSafe + 'px'
              }}
            >
              <ul className="w-full h-full flex justify-between px-[15px]">
                {
                  barList?.map((item) => {
                    return (
                      <FooterTabItem
                        key={item.id}
                        item={item}
                        pathname={pathname}
                        animationMatcher={animationMatcher}
                        onClickLink={handleJump}
                      />
                    );
                  })
                }
              </ul>
            </div>
          </>
        )
      }
    </>
  );
}

interface FooterTabItemProps {
  item: BottomToolbarItem;
  pathname: string;
  animationMatcher: FooterAnimationMatcher | null;
  onClickLink: (e: any, item: BottomToolbarItem) => void;
}

function FooterTabItem({
  item,
  pathname,
  animationMatcher,
  onClickLink,
}: FooterTabItemProps) {
  const [isAnimationReady, setIsAnimationReady] = useState(false);
  const isActive = item.jump_link === pathname;
  const shouldShowAnimation = animationMatcher?.(item.jump_link) ?? false;
  const iconSrc = item.icon_url?.[isActive ? 'selected' : 'default'] ?? '';
  const jumpLink = useMemo(() => {
    if (!item.jump_link) return '';
    const separator = item.jump_link.includes('?') ? '&' : '?';
    return `${item.jump_link}${separator}trackSource=${ActivitySourceEnum.bottomTab}`;
  }, [item.jump_link]);

  useEffect(() => {
    if (!shouldShowAnimation) {
      setIsAnimationReady(false);
    }
  }, [shouldShowAnimation]);

  return (
    <Link
      href={jumpLink}
      className="flex-1"
      prefetch={true}
      onClick={(e) => {
        onClickLink(e, item);
      }}
    >
      <li
        onClick={() => {
          track('tab_click', {
            button_name: item?.name_en || item?.external_name,
          });
        }}
        className="relative whitespace-nowrap flex flex-col items-center justify-center pt-[4px] active:scale-90"
      >
        {isActive && (
          <Image
            src={activeIcon}
            alt="icon"
            width={65}
            height={23}
            className="absolute top-[-1px] left-[50%] translate-x-[-50%]"
          />
        )}
        <div className="relative flex h-[43px] w-[43px] items-center justify-center">
          <Image
            className={clsx("min-h-[19px]", {
              "opacity-0": shouldShowAnimation && isAnimationReady,
            })}
            src={iconSrc}
            alt="icon"
            width={43}
            height={43}
          />
          {shouldShowAnimation && (
            <IplFooterAnimate
              visible={shouldShowAnimation}
              onReadyChange={setIsAnimationReady}
            />
          )}
        </div>
        <div
          className={clsx(
            "absolute bottom-[-8px] left-1/2 -translate-x-1/2 text-tertiary text-xss font-medium",
            {
              "text-white": isActive,
            }
          )}
        >
          {item?.external_name}
        </div>
      </li>
    </Link>
  );
}
